import type { FC } from "react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useJobs } from "../../hooks/useJobs";
import { useRoleColors } from "../../hooks/useRoleColors";
import type { NurseFullRole } from "../../utils/models";
import { FilterFieldList } from "../UIToolkit/Filters/FilterFieldList";
import { FilterSidebar } from "../UIToolkit/Filters/FilterSidebar";
import type { FilterSection } from "../UIToolkit/Filters/filterFields";
import type { TeamFacetCounts } from "./teamFacetFilters";
import {
	buildAgencyOptions,
	buildContractOptions,
	buildJobOptions,
	buildLastLoginOptions,
	buildRoleOptions,
	buildStatusOptions,
	buildUnionOptions,
	NEEDS_DECISION_OPTION_ID,
} from "./teamFilterOptions";

type TeamFilterSidebarProps = {
	activeFilterCount: number;
	onClearAll: () => void;
	facetCounts: TeamFacetCounts;
	collapsed: boolean;
	onCollapsedChange: (collapsed: boolean) => void;
	/**
	 * The group a column's "Filter by…" just sent the user to. That group is force-expanded, scrolled
	 * to and flashed — otherwise the hand-off drops them into a long rail with no clue which group
	 * they were sent to.
	 */
	focusedGroup: string | null;

	/** EffectiveEmployeeStatus values (OR'ed). Empty = all statuses. */
	selectedStatuses: ReadonlySet<string>;
	onStatusesChange: (statuses: Set<string>) => void;
	/** Agency names + NO_AGENCY_FILTER_ID (OR'ed). Empty = no agency restriction. */
	selectedAgencies: ReadonlySet<string>;
	onAgenciesChange: (agencies: Set<string>) => void;
	/** Union names + NO_UNION_FILTER_ID (OR'ed). Empty = no union restriction (the default). */
	selectedUnions: ReadonlySet<string>;
	onUnionsChange: (unions: Set<string>) => void;
	/** Last-login buckets (OR'ed), stalest first. Empty = no last-login restriction. */
	selectedLastLogin: ReadonlySet<string>;
	onLastLoginChange: (buckets: Set<string>) => void;
	showOnlyDecisions: boolean;
	onShowOnlyDecisionsChange: (value: boolean) => void;
	/** Count of open HRIS status decisions — the needs-decision option renders only when > 0. */
	decisionsCount: number;

	selectedRoles: NurseFullRole[] | undefined;
	onRolesChange: (roles: NurseFullRole[]) => void;

	selectedJobNames: string[];
	onJobsChange: (jobNames: string[]) => void;

	selectedSections: ReadonlySet<string>;
	onSectionsChange: (sections: Set<string>) => void;
};

/** Jobs and Agency can run long; the rest are short closed lists that read better fully expanded. */
const LONG_GROUP_VISIBLE_LIMIT = 6;

/**
 * The Team page's faceted filter rail: Status · Role · Job · Contract type · Agency · Union ·
 * Last login.
 *
 * It used to carry ten groups. Integrator, Email, Phone and Auto Approval came out — nobody
 * reached them, and each is better answered as a column than as a facet — so they are now
 * URL-only dimensions (`?email=`, `?integrator=`…) rather than four groups of rail nobody scrolled
 * past. Search left too, for the opposite reason: it is the most-used control on the page and it
 * was the least prominent one, so it now sits full-width above the table.
 *
 * Pure wiring: the option arrays are built by `teamFilterOptions.ts`; this file owns the toggle
 * handlers (for analytics) and the config it hands to the generic `FilterSidebar` + `FilterFieldList`
 * (whose `facet` fields render `FacetFilterSection`).
 */
export const TeamFilterSidebar: FC<TeamFilterSidebarProps> = ({
	activeFilterCount,
	onClearAll,
	facetCounts,
	collapsed,
	onCollapsedChange,
	focusedGroup,
	selectedStatuses,
	onStatusesChange,
	selectedAgencies,
	onAgenciesChange,
	selectedUnions,
	onUnionsChange,
	selectedLastLogin,
	onLastLoginChange,
	showOnlyDecisions,
	onShowOnlyDecisionsChange,
	decisionsCount,
	selectedRoles,
	onRolesChange,
	selectedJobNames,
	onJobsChange,
	selectedSections,
	onSectionsChange,
}) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { departmentNurseTypes, chosenDepartment } = useHospitalData();
	const availableJobs = useJobs();

	const trackToggle = useCallback(
		(group: string, option: string, checked: boolean) => {
			track("team_filter_option_toggled", { department_id: chosenDepartment.department_id, group, option, checked });
		},
		[track, chosenDepartment.department_id],
	);

	const trackSectionToggle = useCallback(
		(group: string) => (expanded: boolean) => {
			track("team_filter_section_toggled", { department_id: chosenDepartment.department_id, group, expanded });
		},
		[track, chosenDepartment.department_id],
	);

	const trackShowAllToggle = useCallback(
		(group: string) => (showAll: boolean) => {
			track("team_filter_show_all_toggled", { department_id: chosenDepartment.department_id, group, show_all: showAll });
		},
		[track, chosenDepartment.department_id],
	);

	// One toggle flow for every Set-backed facet group: clone → add/remove → propagate → track.
	const makeSetToggleHandler = useCallback(
		(group: string, selected: ReadonlySet<string>, onChange: (next: Set<string>) => void) => (id: string, checked: boolean) => {
			const next = new Set(selected);
			if (checked) next.add(id);
			else next.delete(id);
			onChange(next);
			trackToggle(group, id, checked);
		},
		[trackToggle],
	);

	// Array-backed sibling of `makeSetToggleHandler` for the Roles + Jobs groups (order-preserving
	// arrays, not Sets). The generic `T` lets the Roles handler round-trip `NurseFullRole` without a
	// bespoke cast site — option ids for these groups are always valid values of `T`.
	const makeArrayToggleHandler = useCallback(
		<T extends string>(group: string, selected: T[] | undefined, onChange: (next: T[]) => void) =>
			(id: string, checked: boolean) => {
				const current = selected ?? [];
				onChange(checked ? [...current, id as T] : current.filter((value) => value !== id));
				trackToggle(group, id, checked);
			},
		[trackToggle],
	);

	// ── Option arrays (built purely in teamFilterOptions.ts) ──
	const statusOptions = useMemo(
		() => buildStatusOptions({ t, facetCounts, selectedStatuses, showOnlyDecisions, decisionsCount }),
		[t, facetCounts, selectedStatuses, showOnlyDecisions, decisionsCount],
	);
	const roleColor = useRoleColors();
	const roleOptions = useMemo(
		() => buildRoleOptions({ facetCounts, departmentNurseTypes, selectedRoles, roleColor }),
		[facetCounts, departmentNurseTypes, selectedRoles, roleColor],
	);
	const jobOptions = useMemo(() => buildJobOptions({ t, facetCounts, availableJobs, selectedJobNames }), [t, facetCounts, availableJobs, selectedJobNames]);
	const contractOptions = useMemo(() => buildContractOptions({ facetCounts, selectedSections }), [facetCounts, selectedSections]);
	const agencyOptions = useMemo(() => buildAgencyOptions({ t, facetCounts, selectedAgencies }), [t, facetCounts, selectedAgencies]);
	const unionOptions = useMemo(() => buildUnionOptions({ t, facetCounts, selectedUnions }), [t, facetCounts, selectedUnions]);
	const lastLoginOptions = useMemo(() => buildLastLoginOptions({ t, facetCounts, selectedLastLogin }), [t, facetCounts, selectedLastLogin]);

	// ── Toggle handlers ──
	const toggleStatuses = useMemo(
		() => makeSetToggleHandler("status", selectedStatuses, onStatusesChange),
		[makeSetToggleHandler, selectedStatuses, onStatusesChange],
	);
	const handleStatusToggle = useCallback(
		(id: string, checked: boolean) => {
			// The needs-decision option is a view toggle, not a status value — route it separately.
			if (id === NEEDS_DECISION_OPTION_ID) {
				onShowOnlyDecisionsChange(checked);
				trackToggle("status", id, checked);
			} else toggleStatuses(id, checked);
		},
		[toggleStatuses, onShowOnlyDecisionsChange, trackToggle],
	);
	const handleRoleToggle = useMemo(
		() => makeArrayToggleHandler<NurseFullRole>("roles", selectedRoles, onRolesChange),
		[makeArrayToggleHandler, selectedRoles, onRolesChange],
	);
	const handleJobToggle = useMemo(
		() => makeArrayToggleHandler("jobs", selectedJobNames, onJobsChange),
		[makeArrayToggleHandler, selectedJobNames, onJobsChange],
	);
	const handleContractToggle = useMemo(
		() => makeSetToggleHandler("contract", selectedSections, onSectionsChange),
		[makeSetToggleHandler, selectedSections, onSectionsChange],
	);
	const handleAgencyToggle = useMemo(
		() => makeSetToggleHandler("agency", selectedAgencies, onAgenciesChange),
		[makeSetToggleHandler, selectedAgencies, onAgenciesChange],
	);
	const handleUnionToggle = useMemo(
		() => makeSetToggleHandler("union", selectedUnions, onUnionsChange),
		[makeSetToggleHandler, selectedUnions, onUnionsChange],
	);
	const handleLastLoginToggle = useMemo(
		() => makeSetToggleHandler("last_login", selectedLastLogin, onLastLoginChange),
		[makeSetToggleHandler, selectedLastLogin, onLastLoginChange],
	);

	const handleClearAll = useCallback(() => {
		onClearAll();
		track("team_filters_cleared", { department_id: chosenDepartment.department_id });
	}, [onClearAll, track, chosenDepartment.department_id]);

	/**
	 * One `facet` field per group — `group` drives both the test id (`team-filter-<group>`) and the
	 * analytics callbacks, so the render stays a single config and adding a group is one entry here.
	 *
	 * Agency and Union start collapsed on purpose: both carry dirty data (an "Unnamed agency" bucket,
	 * and union values that differ only by capitalization) and neither is a daily filter, so the mess
	 * shouldn't be the first thing anyone sees. Reopening one is a click.
	 *
	 * Each group is its own section rather than several fields in one, because `FilterFieldList` puts
	 * the hairline divider BETWEEN sections — which is the separation this rail has always had.
	 */
	const sections: FilterSection[] = [
		{ group: "status", title: t("team.filter.group.status"), options: statusOptions, onToggle: handleStatusToggle },
		{ group: "roles", title: t("team.filter.group.roles"), options: roleOptions, onToggle: handleRoleToggle },
		// 22 values on some departments, so this one earns a search box.
		{ group: "jobs", title: t("jobs"), options: jobOptions, onToggle: handleJobToggle, searchable: true, visibleLimit: LONG_GROUP_VISIBLE_LIMIT },
		{ group: "contract", title: t("contract.type"), options: contractOptions, onToggle: handleContractToggle },
		{
			group: "agency",
			title: t("team.filter.group.agency"),
			options: agencyOptions,
			onToggle: handleAgencyToggle,
			defaultExpanded: false,
			searchable: true,
			visibleLimit: LONG_GROUP_VISIBLE_LIMIT,
		},
		{ group: "union", title: t("team.filter.group.union"), options: unionOptions, onToggle: handleUnionToggle, defaultExpanded: false },
		{ group: "last_login", title: t("team.filter.group.last_login"), options: lastLoginOptions, onToggle: handleLastLoginToggle },
	].map(({ group, title, options, onToggle, defaultExpanded, searchable, visibleLimit }) => ({
		id: group,
		fields: [
			{
				kind: "facet" as const,
				id: group,
				title,
				options,
				onToggle,
				defaultExpanded,
				searchable,
				visibleLimit,
				// `highlighted` opens the group as part of the jump and leaves it open. Forcing `expanded`
				// here instead would only hold it open until the flash ended.
				highlighted: focusedGroup === group,
				// Unchecked options read as "+6" while this group has a selection. Status and Agency ship
				// pre-checked, so their sibling counts sit OUTSIDE the visible result — with raw numbers the
				// group appears to hold more people than the table shows, which is what made the whole rail
				// look untrustworthy.
				countMode: "additive" as const,
				foldZeroCounts: true,
				onExpandedChange: trackSectionToggle(group),
				onShowAllChange: trackShowAllToggle(group),
				dataTestId: `team-filter-${group}`,
			},
		],
	}));

	return (
		<FilterSidebar
			collapsed={collapsed}
			onCollapsedChange={onCollapsedChange}
			// The page-level rail collapses to a full-height strip rather than a pill, so the table keeps
			// its position when the rail opens and closes.
			collapsedVariant="rail"
			// The page frames the rail and the table together, so the rail draws only the divider between
			// them — two separately-framed panels with a gap between them read as two unrelated things,
			// when the rail exists to drive the table.
			frame="flush"
			activeFilterCount={activeFilterCount}
			onClearAll={handleClearAll}
			dataTestId="team-filter-sidebar"
		>
			<FilterFieldList sections={sections} dividers dataTestId="team-filter" />
		</FilterSidebar>
	);
};
